import React from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import Footer from "../components/Footer";
import { ArrowLeft, FileText } from "lucide-react";

const Section = ({ title, children, id }) => (
  <section id={id} className="mb-10">
    <h2 className="font-display text-2xl text-[#F5F2EE] mb-3">{title}</h2>
    <div className="text-[#DCD7D2] text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const Terms = () => (
  <div className="min-h-screen bg-[#0C0E12]" data-testid="terms-page">
    <SiteHeader />
    <main className="max-w-3xl mx-auto px-6 md:px-12 py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-[#A29F9B] hover:text-[#C5A880] mb-8">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-[#C5A880]" />
        <span className="label-uppercase">Documento legal</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl mb-2">Termos e Condições</h1>
      <p className="text-[#A29F9B] mb-10">Última atualização: 28 de Abril de 2026</p>

      <Section title="1. Identificação do prestador" id="prestador">
        <p>
          A plataforma <strong>Soulmatch</strong> é operada por <strong>Solfut, Lda</strong> (adiante "Solfut"),
          pessoa colectiva nº <strong>503 928 518</strong>, com sede em Edifício Camões, Rua de Camões nº 117, 6º andar, 4000-144 Porto, Portugal.
          Contacto: <a className="text-[#C5A880] hover:underline" href="mailto:servico-clientes@solfut.com">servico-clientes@solfut.com</a> · +351 224 053 270.
        </p>
      </Section>

      <Section title="2. Objecto" id="objecto">
        <p>
          Os presentes Termos regulam o acesso e utilização da plataforma Soulmatch, um serviço online de matchmaking
          baseado em afinidades de personalidade, valores e estilo de vida (adiante "Serviço").
          Ao registares-te, declaras ter <strong>idade igual ou superior a 18 anos</strong> e capacidade legal para contratar.
        </p>
      </Section>

      <Section title="3. Conta de utilizador" id="conta">
        <p>
          Comprometes-te a fornecer informação verdadeira e a manter as tuas credenciais em segurança.
          A Solfut reserva-se o direito de suspender ou eliminar contas que (a) violem estes Termos,
          (b) usem fotografias ou dados de terceiros, (c) prossigam fins ilícitos, comerciais não autorizados, ou comportamentos abusivos para com outros utilizadores.
        </p>
      </Section>

      <Section title="4. Subscrição Premium e pagamentos" id="premium">
        <p>
          A Soulmatch disponibiliza funcionalidades gratuitas e funcionalidades Premium pagas (planos Mensal e Anual).
          Os pagamentos são processados pela <strong>EuPago (SIBS)</strong>, em ambiente seguro e certificado PSD2.
          A Solfut não armazena dados de cartão.
        </p>
        <p>
          <strong>Toda a venda é faturada</strong>. Após confirmação de pagamento, é emitida automaticamente uma
          <strong> Fatura</strong> seguida de um <strong>Recibo</strong> (FT + RC) através do sistema certificado <strong>Moloni</strong>,
          em nome da Solfut, Lda. A Fatura+Recibo fica disponível para download em PDF na zona "Faturação" da tua conta
          e é enviada por email. O NIF é opcional — se não for indicado, a fatura é emitida em nome de "Consumidor Final".
        </p>
        <p>
          Os preços são apresentados em Euros e incluem IVA à taxa legal em vigor (23%).
          A Solfut reserva-se o direito de alterar preços para futuras subscrições, sem efeitos retroactivos.
        </p>
      </Section>

      <Section title="5. Renovação e cancelamento" id="cancelamento">
        <p>
          Os planos da Soulmatch são <strong>compras únicas</strong> com período fixo (30 ou 365 dias).
          Não há renovação automática nem cobrança recorrente sem a tua autorização explícita.
          Podes deixar de subscrever a qualquer momento — manténs o acesso Premium até ao fim do período pago.
        </p>
      </Section>

      <Section title="6. Direito de livre resolução e reembolsos" id="reembolsos">
        <p>
          Por se tratar de um serviço digital cuja execução começa imediatamente após a confirmação do pagamento,
          poderás perder o direito de livre resolução nos termos do art.º 17º, nº 1, alínea m) do
          Decreto-Lei nº 24/2014. Ainda assim, a Solfut adopta uma política de reembolso flexível —
          consulta a <Link to="/reembolsos" className="text-[#C5A880] hover:underline">Política de Reembolso</Link>.
        </p>
      </Section>

      <Section title="7. Conduta do utilizador" id="conduta">
        <p>
          É proibido: (a) assediar, intimidar ou ameaçar outros utilizadores;
          (b) publicar conteúdo difamatório, discriminatório ou sexualmente explícito não consentido;
          (c) fazer-se passar por outra pessoa;
          (d) usar a plataforma para fins comerciais, recrutamento ou prostituição;
          (e) fazer scraping ou engenharia reversa do Serviço.
          Violações resultam em suspensão imediata sem reembolso.
        </p>
      </Section>

      <Section title="8. Propriedade intelectual" id="ip">
        <p>
          O software, design, marcas e algoritmo de matchmaking da Soulmatch são propriedade da Solfut, Lda.
          Os conteúdos que publicas (fotos, biografias, respostas ao questionário) continuam a ser teus —
          concedes à Solfut uma licença não-exclusiva, mundial e gratuita para os usar exclusivamente para operar o Serviço.
        </p>
      </Section>

      <Section title="9. Limitação de responsabilidade" id="responsabilidade">
        <p>
          A Soulmatch é uma plataforma de facilitação de encontros — <strong>não garante a obtenção de uma relação</strong>,
          nem se responsabiliza pela conduta de outros utilizadores, encontros presenciais ou interacções fora da plataforma.
          Encoraja-se sempre encontros em locais públicos. Em caso de denúncia, contactar imediatamente
          servico-clientes@solfut.com ou as autoridades competentes.
        </p>
      </Section>

      <Section title="10. Resolução de litígios" id="litigios">
        <p>
          Em caso de litígio de consumo, o consumidor pode recorrer à entidade de Resolução Alternativa de Litígios:
          <strong> CNIACC — Centro Nacional de Informação e Arbitragem de Conflitos de Consumo</strong>,
          {" "}<a className="text-[#C5A880] hover:underline" href="https://www.cniacc.pt" target="_blank" rel="noopener noreferrer">www.cniacc.pt</a>.
          A lei aplicável é a portuguesa, com jurisdição da Comarca do Porto.
        </p>
      </Section>

      <Section title="11. Alterações aos Termos" id="alteracoes">
        <p>
          A Solfut pode actualizar estes Termos. Alterações materiais serão comunicadas por email com 30 dias de antecedência.
          Continuar a usar o Serviço após a entrada em vigor implica aceitação.
        </p>
      </Section>

      <p className="text-xs text-[#A29F9B] mt-12 pt-6 border-t border-white/5">
        © 2026 Solfut, Lda · Versão 1.0 · 28-04-2026
      </p>
    </main>
    <Footer />
    </div>
);

export default Terms;
